import { useEffect, useReducer } from 'react';

import dxWorkSectionService from '../../../services/dxworksectionservice';
import commentService from '../../../services/commentservice';
import workflowService from '../../../services/workflowservice';
import holdSectionService from '../../../components/holdsection/service';
import makeAuditResolutionService from '../../../services/auditreasonresolutionservice';
import { useDxWorkSection } from '../../hooks/dxworksection';
import { useAddDxDialog } from '../../hooks/adddxdialog';
import { useAddCommentDialog } from '../../hooks/addcommentdialog';
import { useAddReasonOptionDialog } from '../../hooks/addreasonoptiondialog';
import { useLookback } from '../../hooks/lookback';
import { useCommentSection } from '../../hooks/commentsection';
import { useWorkflowSection } from '../../hooks/workflowsection';
import { useOnHoldDialog } from '../../hooks/onholddialog';
import { useHoldReviewDialog } from '../../hooks/holdreviewdialog';
import { useAuditResolutionDialog } from '../../hooks/auditresolutiondialog';
import useHoldSection from '../../../components/holdsection/hook';
import wubs from '../../../services/wubwub';
import * as ACTIONS from './actions';
import detailInitialState from './initialstate';
import detailReducer from './reducer';

import useBroadcastChannel from '../../../components/popoutcard/usebroadcastchannel';

/* useCodingQueueDetailsService Hook. Contains all behavior necessary for
interacting with the service for the Coding Queue Details screen. */
const useCodingQueueDetails = (service, encId) => {
  const [state, dispatch] = useReducer(detailReducer, detailInitialState);

  const dxWorkService = dxWorkSectionService(service.serviceRoutes);
  const commentSectionService = commentService(service.serviceRoutes);
  const workflowSectionService = workflowService(service.serviceRoutes);
  const holdDataService = holdSectionService(service.serviceRoutes);
  const auditResolutionService = makeAuditResolutionService(service.serviceRoutes);

  const [dxSectionState, dxSectionActions] = useDxWorkSection(dxWorkService);
  const [addDxDialogState, addDxDialogActions] = useAddDxDialog();
  const [addCommentDialogState, addCommentDialogActions] = useAddCommentDialog();
  const [
    addReasonOptionDialogState,
    addReasonOptionDialogActions
  ] = useAddReasonOptionDialog();
  const [lookbackState, lookbackActions] = useLookback();
  const [
    commentSectionState,
    commentSectionActions
  ] = useCommentSection(commentSectionService, encId);
  const [
    workflowState,
    workflowActions
  ] = useWorkflowSection(workflowSectionService);
  const [holdDialogState, holdDialogActions] = useOnHoldDialog();
  const [holdReviewDialogState, holdReviewDialogActions] = useHoldReviewDialog();
  const [holdSectionState, holdSectionActions] = useHoldSection(holdDataService);
  const [
    auditResolutionDialogState,
    auditResolutionDialogActions
  ] = useAuditResolutionDialog(auditResolutionService);

  const dispatchAddCommentToPatient = (patientId, commentText) => {
    commentSectionActions.addPatientComment(patientId, commentText)
      .then(() => {
        addCommentDialogActions.close();
      })
  };

  const dispatchAddCommentToEncounter = (encounterId, commentText) => {
    commentSectionActions.addEncounterComment(encounterId, commentText)
      .then(() => {
        addCommentDialogActions.close();
      });
  }

  const dispatchSaveComment = comment => {
    commentSectionActions.saveComment(comment)
      .then(() => {
        addCommentDialogActions.close();
      });
  }

  const dispatchDeleteComment = commentId => {
    commentSectionActions.deleteComment(commentId)
      .then(() => {
        addCommentDialogActions.close();
      });
  }

  const dispatchSetCommentInactive = comment => {
    commentSectionActions.setCommentInactive(comment)
      .then(() => {
        addCommentDialogActions.close();
      });
  }

  const dispatchAddDx = (
    encounterId,
    cmsCodeId,
    supportingText,
    reasonOptions
  ) => {
    return dxSectionActions.addDx(
      encounterId,
      cmsCodeId,
      supportingText,
      reasonOptions
    )
    .then(() => {
      addDxDialogActions.close();
    });
  };

  const dispatchApproveDx = (encounterId, encounterCmsCodeIds, reasonOptions) => {
    dxSectionActions.approveDx(encounterId, encounterCmsCodeIds, reasonOptions)
      .then(() => {
        addReasonOptionDialogActions.close();
      });
  };

  const dispatchRemoveDx = (
    encounterId,
    encounterCmsCodeIds,
    reasonOptions
  ) => {
    dxSectionActions.removeDx(encounterId, encounterCmsCodeIds, reasonOptions)
      .then(() => {
        addReasonOptionDialogActions.close();
      });
  };

  const placeOnHoldAndOpenAddCommentDialog = () => {
    holdDialogActions.open();
  };

  const submitHold = (encounterId, holdReasonId, comment, doRedirect) => {
    return workflowActions.placeOnHold(
      encounterId,
      holdReasonId,
      comment,
      doRedirect
    );
  };

  const submitHoldReview = (encounterId, comment) => {
    return holdSectionActions.markAsReviewed(encounterId, comment);
  };

  const hydrateEncounterAuditReasons = encounterAuditReasons => {
    dispatch(ACTIONS.hydrateEncounterAuditReasons(encounterAuditReasons));
  };

  const boundActions = {
    showLookback: lookbackActions.show,
    hideLookback: lookbackActions.hide,
    setLookbackFilterValue: lookbackActions.setFilterValue,
    placeOnHold: placeOnHoldAndOpenAddCommentDialog,
    submitHold: submitHold,
    closeHoldDialog: holdDialogActions.close,
    removeFromHold: workflowActions.removeFromHold,
    addCommentToPatient: dispatchAddCommentToPatient,
    addCommentToEncounter: dispatchAddCommentToEncounter,
    saveComment: dispatchSaveComment,
    deleteComment: dispatchDeleteComment,
    setCommentInactive: dispatchSetCommentInactive,
    openAddCommentDialog: addCommentDialogActions.open,
    closeAddCommentDialog: addCommentDialogActions.close,
    openAddReasonOptionDialog: addReasonOptionDialogActions.open,
    closeAddReasonOptionDialog: addReasonOptionDialogActions.close,
    approveDx: dispatchApproveDx,
    unapproveDx: dxSectionActions.unapproveDx,
    removeDx: dispatchRemoveDx,
    unremoveDx: dxSectionActions.unremoveDx,
    openAddDxDialog: addDxDialogActions.open,
    closeAddDxDialog: addDxDialogActions.close,
    addDx: dispatchAddDx,
    finalizeEncounter: workflowActions.finalizeEncounter,
    unfinalizeEncounter: workflowActions.unfinalizeEncounter,
    exitEncounter: workflowActions.exitEncounter,
    completeEncounter: workflowActions.completeEncounter,
    toggleEncounterAuditReason: dxSectionActions.toggleEncounterAuditReason,
    redirectToReviewDetails: service.serviceRoutes.redirectToReviewDetails,
    beginMultiselect: dxSectionActions.beginMultiselect,
    endMultiselect: dxSectionActions.endMultiselect,
    selectItemMultiselect: dxSectionActions.selectItemMultiselect,
    deselectItemMultiselect: dxSectionActions.deselectItemMultiselect,
    markAsReviewed: submitHoldReview,
    openHoldReviewDialog: holdReviewDialogActions.open,
    closeHoldReviewDialog: holdReviewDialogActions.close,
    openAuditResolutionDialog: auditResolutionDialogActions.open,
    closeAuditResolutionDialog: auditResolutionDialogActions.close,
    resolveAuditReason: auditResolutionDialogActions.resolveAuditReason,
    hydrateEncounterAuditReasons,
    selectAuditResolutionOption:
      auditResolutionDialogActions.selectAuditResolutionOption
  };

  const broadcastHandler = channel => e => {
    const message = pluckMessage(e);

    switch (message.type) {
      case 'RELOAD_ENCOUNTER': {
        const { payload: { encounterId } } = message;
        dispatch(ACTIONS.initRequest());
        service.init(encounterId)
          .then(result => {
            dispatch(ACTIONS.initSuccess(result));
            // hydrate various slices of state that are interested in this data
            dxSectionActions.hydrate(result.dxList.listItems);
            commentSectionActions.hydrate(result.comments);
            workflowActions.hydrate(
              result.currentEncounterInfo.isOnHold,
              result.currentEncounterInfo.isFinalized
            );

            holdSectionActions.hydrate(result.holdData);

            hydrateEncounterAuditReasons(result.encounterAuditReasons);
          })
          .catch(e => dispatch(ACTIONS.initFailure(e.toString())));

        return;
      }

      default: {
        return;
      }
    }
  };

  const [postMessage] = useBroadcastChannel('foo', broadcastHandler);

  // fire off init
  useEffect(() => {
    dispatch(ACTIONS.initRequest());
    postMessage({
      type: 'SHOW_LOADING',
      payload: { statusMessage: 'Reloading Encounter' }
    });
    service.init(encId)
      .then(result => {
        dispatch(ACTIONS.initSuccess(result));

        postMessage(
          {
            type: 'RELOAD_ENCOUNTER',
            payload: { encounterId: encId }
          });

        // hydrate various slices of state that are interested in this data
        dxSectionActions.hydrate(result.dxList.listItems, result.encounterAuditReasons);
        commentSectionActions.hydrate(result.comments);
        workflowActions.hydrate(
          result.currentEncounterInfo.isOnHold,
          result.currentEncounterInfo.isFinalized
        );

        holdSectionActions.hydrate(result.holdData);
        auditResolutionDialogActions.hydrate(result.auditResolutionOptions);
      })
      .catch(e => dispatch(ACTIONS.initFailure(e.toString())));
  }, [encId]);

  // wub wub
  useEffect(() => {
    const refreshWubs = () => {
      if (!wubs.isInitialized) {
        wubs.init();
        wubs.setAsInitialized();
      }

      wubs.setEncodings(dxSectionState.dxList.listItems);
      wubs.highlightEncodings(wubs.hexCodes);
      wubs.toggleExclusionHighlight();
    };

    requestAnimationFrame(refreshWubs);
  }, [
    dxSectionState.dxList.listItems.length,
    lookbackState.isVisible,
    state.isLoading
  ]);

  const isPageLoading = (
    dxSectionState.isLoading
    || commentSectionState.isLoading
    || workflowState.isLoading
    || auditResolutionDialogState.isLoading
    || state.isLoading
  );

  const getLoadingMessage = () => {
    const hasMessage = s => s.loadingMessage.length > 0;

    if (commentSectionState.isLoading) return commentSectionState.loadingMessage;
    if (dxSectionState.isLoading) return dxSectionState.loadingMessage;
    if (workflowState.isLoading) return workflowState.loadingMessage;
    if (auditResolutionDialogState.isLoading)
      return auditResolutionDialogState.loadingMessage

    return state.loadingMessage;
  };

  return [
    {
      ...state,
      isLoading: isPageLoading,
      loadingMessage: getLoadingMessage(),
      details: {
        ...state.details,
        currentEncounterInfo: {
          ...state.details.currentEncounterInfo,
          isOnHold: workflowState.isOnHold,
          isFinalized: workflowState.isFinalized
        },
        dxList: {
          ...dxSectionState.dxList,
          isMultiselect: dxSectionState.isMultiselect,
          multiselectItems: dxSectionState.multiselectItems
        },
        comments: commentSectionState.comments,
        holdData: holdSectionState
      },
      addDxDialog: addDxDialogState,
      addCommentDialog: addCommentDialogState,
      addReasonOptionDialog: addReasonOptionDialogState,
      lookback: lookbackState,
      holdDialog: holdDialogState,
      holdReviewDialog: holdReviewDialogState,
      auditResolutionDialog: auditResolutionDialogState
    },
    boundActions
  ];
};

export default useCodingQueueDetails;
